import { useState } from "react";
import { Button } from "@/components/ui/button";
import { User, Calendar, HelpCircle, LogOut, ChevronDown, Check, Home, Settings } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites } from "@/hooks/useFavorites";
import { useProviders } from "@/hooks/useProviders";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useDomainProvider } from "@/hooks/useDomainProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ProviderBrandedHeaderProps {
  provider: {
    id: string;
    name: string;
    display_name?: string | null;
    image_url?: string | null;
    slug?: string;
    brand_primary_color?: string | null;
    brand_secondary_color?: string | null;
    brand_font?: string | null;
    brand_header_text_color?: string | null;
    brand_header_font_weight?: string | null;
    brand_header_icon_text_color?: string | null;
    brand_header_button_text_color?: string | null;
    brand_header_letter_spacing?: string | null;
  };
}

const ProviderBrandedHeader = ({ provider }: ProviderBrandedHeaderProps) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { favorites, setAsFavorite, isSettingFavorite } = useFavorites();
  const { data: providers = [] } = useProviders();
  const { data: providerAccess } = useProviderAccess();
  const { isDomainLocked } = useDomainProvider();
  const [providerSwitcherOpen, setProviderSwitcherOpen] = useState(false);

  // Get favorite provider ID for comparison
  const favoriteProviderId = favorites.length > 0 ? favorites[0]?.provider_id : null;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleSelectProvider = (selectedProvider: typeof providers[0]) => {
    setAsFavorite(selectedProvider.id);
    setProviderSwitcherOpen(false);
    // Navigate to the selected provider's page
    if (selectedProvider.slug) {
      navigate(`/provider/${selectedProvider.slug}`);
    }
  };

  const userName = user?.user_metadata?.first_name && user?.user_metadata?.last_name
    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
    : user?.email || "User";
  
  const userEmail = user?.email || "";

  // Check if provider has custom branding
  const hasBranding = provider.brand_primary_color;
  const primaryColor = provider.brand_primary_color || undefined;
  const secondaryColor = provider.brand_secondary_color || undefined;
  const brandFont = provider.brand_font || undefined;
  const headerTextColor = provider.brand_header_text_color || undefined;
  const headerFontWeight = provider.brand_header_font_weight || undefined;
  const headerIconTextColor = provider.brand_header_icon_text_color || undefined;
  const headerButtonTextColor = provider.brand_header_button_text_color || undefined;
  const headerLetterSpacing = provider.brand_header_letter_spacing || undefined;

  // Determine text color based on background (simple light/dark check)
  const getContrastColor = (hexColor: string | undefined) => {
    if (!hexColor) return undefined;
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Use custom header text color if defined, otherwise fall back to auto-contrast
  const textColor = headerTextColor || getContrastColor(primaryColor);

  return (
    <header 
      className="sticky top-0 left-0 right-0 z-50 border-b"
      style={{ 
        backgroundColor: primaryColor || 'hsl(var(--background))',
        borderColor: hasBranding ? 'transparent' : 'hsl(var(--border))',
        fontFamily: brandFont
      }}
    >
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center py-3">
          {/* Provider header - show switcher only if not domain locked */}
          {isDomainLocked ? (
            // When domain is locked, just show the provider name without switcher
            <div className="flex items-center gap-2">
              {provider.image_url ? (
                <img 
                  src={provider.image_url}
                  alt={provider.name} 
                  className="h-8 w-8 rounded-lg object-cover"
                />
              ) : (
                <div 
                  className="h-8 w-8 rounded-lg flex items-center justify-center"
                  style={{ 
                    backgroundColor: secondaryColor || (hasBranding ? 'rgba(255,255,255,0.2)' : 'hsl(var(--foreground))'),
                    color: textColor || 'hsl(var(--background))'
                  }}
                >
                  <span className="font-bold text-sm">
                    {provider.name.charAt(0)}
                  </span>
                </div>
              )}
              <span 
                className="text-sm sm:text-base truncate max-w-[150px] sm:max-w-none"
                style={{ 
                  color: textColor || 'hsl(var(--foreground))',
                  fontWeight: headerFontWeight || '600',
                  letterSpacing: headerLetterSpacing || undefined
                }}
              >
                {provider.display_name || provider.name}
              </span>
            </div>
          ) : (
            // Normal provider switcher when not domain locked
            <Popover open={providerSwitcherOpen} onOpenChange={setProviderSwitcherOpen}>
              <PopoverTrigger asChild>
                <button 
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  disabled={isSettingFavorite}
                >
                  {provider.image_url ? (
                    <img 
                      src={provider.image_url}
                      alt={provider.name} 
                      className="h-8 w-8 rounded-lg object-cover"
                    />
                  ) : (
                    <div 
                      className="h-8 w-8 rounded-lg flex items-center justify-center"
                      style={{ 
                        backgroundColor: secondaryColor || (hasBranding ? 'rgba(255,255,255,0.2)' : 'hsl(var(--foreground))'),
                        color: textColor || 'hsl(var(--background))'
                      }}
                    >
                      <span className="font-bold text-sm">
                        {provider.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span 
                    className="text-sm sm:text-base truncate max-w-[150px] sm:max-w-none"
                    style={{ 
                      color: textColor || 'hsl(var(--foreground))',
                      fontWeight: headerFontWeight || '600',
                      letterSpacing: headerLetterSpacing || undefined
                    }}
                  >
                    {provider.display_name || provider.name}
                  </span>
                  <ChevronDown 
                    className="h-4 w-4" 
                    style={{ color: textColor || 'hsl(var(--muted-foreground))' }}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1.5 mb-1">
                  Switch provider
                </div>
                <div className="max-h-64 overflow-y-auto space-y-0.5">
                  {providers.filter(p => !p.is_hidden || p.id === provider.id).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handleSelectProvider(p)}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted text-left transition-colors"
                    >
                      {p.image_url ? (
                        <img 
                          src={p.image_url}
                          alt={p.name} 
                          className="h-8 w-8 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-muted-foreground">
                            {p.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{p.location}</p>
                      </div>
                      {provider.id === p.id && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
          
          {/* User menu */}
          <div className="flex items-center space-x-2">
            {providerAccess && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="font-medium rounded-full hover:bg-black/10"
                style={{ color: textColor }}
              >
                <Link to="/management">
                  <Settings className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Management</span>
                </Link>
              </Button>
            )}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="h-9 w-9 rounded-full p-0"
                    style={{ color: textColor }}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.user_metadata?.avatar_url} alt={userName} />
                      <AvatarFallback 
                        className="font-semibold text-xs"
                        style={{
                          backgroundColor: secondaryColor || 'hsl(var(--foreground))',
                          color: headerIconTextColor || textColor || 'hsl(var(--background))'
                        }}
                      >
                        {userName.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-card shadow-xl border border-border rounded-xl" align="end">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="font-semibold text-foreground">{userName}</p>
                    <p className="text-sm text-muted-foreground">{userEmail}</p>
                  </div>
                  
                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link to={`/provider/${provider.slug}`} className="w-full flex items-center">
                      <Home className="h-4 w-4 mr-3 text-muted-foreground" />
                      Home
                    </Link>
                  </DropdownMenuItem>
                  
                  {providerAccess && (
                    <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3 sm:hidden">
                      <Link to="/management" className="w-full flex items-center">
                        <Settings className="h-4 w-4 mr-3 text-muted-foreground" />
                        Management
                      </Link>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link to="/profile" className="w-full flex items-center">
                      <User className="h-4 w-4 mr-3 text-muted-foreground" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  
                  {!providerAccess && (
                    <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                      <Link to="/dashboard" className="w-full flex items-center">
                        <Calendar className="h-4 w-4 mr-3 text-muted-foreground" />
                        My Bookings
                      </Link>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link to="/support" className="w-full flex items-center">
                      <HelpCircle className="h-4 w-4 mr-3 text-muted-foreground" />
                      Support
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    className="hover:bg-muted cursor-pointer py-3"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-3 text-muted-foreground" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="font-medium rounded-full"
                  style={{ color: textColor }}
                  onClick={() => {
                    localStorage.setItem('pendingFavoriteProvider', provider.id);
                    navigate(`/auth?provider=${provider.id}`);
                  }}
                >
                  Log in
                </Button>
                <Button 
                  size="sm" 
                  className="rounded-full font-medium"
                  style={{ 
                    backgroundColor: secondaryColor || (hasBranding ? 'rgba(255,255,255,0.9)' : undefined),
                    color: headerButtonTextColor || (hasBranding ? primaryColor : undefined)
                  }}
                  onClick={() => {
                    localStorage.setItem('pendingFavoriteProvider', provider.id);
                    navigate(`/auth?provider=${provider.id}`);
                  }}
                >
                  Sign up
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default ProviderBrandedHeader;
